import React, { useState } from "react";
import { Card, Title, Text, Badge, Divider } from "@mantine/core";
import { SimplePatientSelector } from "./SimplePatientSelector";
import type { ExtendedPatientSummary } from "../../types/api";

interface PatientSelectorDemoProps {
  className?: string;
}

export const PatientSelectorDemo: React.FC<PatientSelectorDemoProps> = ({
  className = "",
}) => {
  const [selectedPatient, setSelectedPatient] =
    useState<ExtendedPatientSummary | null>(null);
  const [selectionHistory, setSelectionHistory] = useState<
    ExtendedPatientSummary[]
  >([]);

  const handlePatientSelect = (patient: ExtendedPatientSummary | null) => {
    if (patient) {
      setSelectedPatient(patient);
      // Add to history (avoid duplicates)
      setSelectionHistory((prev) => {
        const filtered = prev.filter((p) => p.patientId !== patient.patientId);
        return [patient, ...filtered].slice(0, 5); // Keep last 5 selections
      });
    } else {
      setSelectedPatient(null);
    }
  };

  const getTotalDataPoints = (patient: ExtendedPatientSummary): number => {
    return (
      patient.glucoseReadings +
      patient.ecgReadings +
      patient.insulinMeasurements +
      patient.mealImages
    );
  };

  const getCohortColor = (condition: string): string => {
    return condition.toLowerCase().includes("diabetes") ? "orange" : "green";
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div>
        <Title order={2}>Enhanced Patient Selector</Title>
        <Text color="dimmed" size="sm" mt={4}>
          Advanced search and filtering for patient selection with real-time
          data indicators
        </Text>
      </div>

      {/* Main Selector */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          Search Patients
        </Title>
        <SimplePatientSelector
          onPatientSelect={handlePatientSelect}
          selectedPatientId={selectedPatient?.patientId}
          placeholder="Search patients..."
          showDataIndicators={true}
          maxResults={20}
        />
      </Card>

      {/* Selected Patient Details */}
      {selectedPatient && (
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <div className="flex items-center justify-between mb-4">
            <Title order={4}>Selected Patient</Title>
            <Badge
              color={getCohortColor(selectedPatient.condition)}
              variant="light"
            >
              {selectedPatient.condition.toLowerCase().includes("diabetes")
                ? "Diabetes"
                : "Healthy"}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Text size="xs" color="dimmed">
                Patient ID
              </Text>
              <Text fw={600}>{selectedPatient.patientId}</Text>
            </div>
            <div>
              <Text size="xs" color="dimmed">
                Name
              </Text>
              <Text fw={600}>{selectedPatient.name}</Text>
            </div>
            <div>
              <Text size="xs" color="dimmed">
                Age / Gender
              </Text>
              <Text fw={600}>
                {selectedPatient.age}y / {selectedPatient.gender}
              </Text>
            </div>
            <div>
              <Text size="xs" color="dimmed">
                Condition
              </Text>
              <Text fw={600}>{selectedPatient.condition}</Text>
            </div>
          </div>

          <Divider my="md" />

          <div>
            <Text size="sm" fw={600} mb="md">
              Data Availability
            </Text>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {selectedPatient.glucoseReadings.toLocaleString()}
                </div>
                <Text size="xs" color="dimmed">
                  Glucose Readings
                </Text>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {selectedPatient.ecgReadings.toLocaleString()}
                </div>
                <Text size="xs" color="dimmed">
                  ECG Readings
                </Text>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {selectedPatient.insulinMeasurements.toLocaleString()}
                </div>
                <Text size="xs" color="dimmed">
                  Insulin Measurements
                </Text>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {selectedPatient.mealImages.toLocaleString()}
                </div>
                <Text size="xs" color="dimmed">
                  Meal Images
                </Text>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <Text size="sm" color="dimmed">
                Total Data Points
              </Text>
              <Text size="lg" fw={600}>
                {getTotalDataPoints(selectedPatient).toLocaleString()}
              </Text>
            </div>
            <div className="flex justify-between items-center mt-2">
              <Text size="sm" color="dimmed">
                Last Updated
              </Text>
              <Text size="sm">
                {new Date(selectedPatient.lastUpdated).toLocaleDateString()}
              </Text>
            </div>
          </div>
        </Card>
      )}

      {/* Selection History */}
      {selectionHistory.length > 0 && (
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Recent Selections
          </Title>
          <div className="space-y-2">
            {selectionHistory.map((patient, index) => (
              <div
                key={patient.patientId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => handlePatientSelect(patient)}
              >
                <div className="flex items-center gap-3">
                  <Text size="sm" fw={600} color="dimmed">
                    #{index + 1}
                  </Text>
                  <div>
                    <Text size="sm" fw={600}>
                      {patient.patientId} - {patient.name}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {patient.age}y • {patient.gender} • {patient.condition}
                    </Text>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    color={getCohortColor(patient.condition)}
                    variant="light"
                    size="xs"
                  >
                    {getTotalDataPoints(patient)} data points
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Features Overview */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          Features
        </Title>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Text fw={600} mb="xs">
              🔍 Advanced Search
            </Text>
            <Text size="sm" color="dimmed">
              Real-time search by patient ID, name, or condition with
              intelligent ranking
            </Text>
          </div>
          <div>
            <Text fw={600} mb="xs">
              🎯 Smart Filters
            </Text>
            <Text size="sm" color="dimmed">
              Filter by diabetes type, cohort, data availability, and minimum
              data points
            </Text>
          </div>
          <div>
            <Text fw={600} mb="xs">
              📊 Data Indicators
            </Text>
            <Text size="sm" color="dimmed">
              Visual indicators showing glucose, ECG, insulin, and meal data
              counts
            </Text>
          </div>
          <div>
            <Text fw={600} mb="xs">
              ♿ Accessibility
            </Text>
            <Text size="sm" color="dimmed">
              Full ARIA support, keyboard navigation, and screen reader
              compatibility
            </Text>
          </div>
          <div>
            <Text fw={600} mb="xs">
              📱 Responsive Design
            </Text>
            <Text size="sm" color="dimmed">
              Optimized for both mobile and desktop experiences
            </Text>
          </div>
          <div>
            <Text fw={600} mb="xs">
              ⚡ Performance
            </Text>
            <Text size="sm" color="dimmed">
              Debounced search, efficient filtering, and optimized rendering
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PatientSelectorDemo;
